import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShopifyProduct, formatPrice, fetchSingleProductVariants } from "@/lib/shopify";
import { useLanguage } from "@/contexts/LanguageContext";
import { getFinishStyle, isDualFinish, getDeanteFinishImage } from "@/lib/finishStyles";
import { WishlistButton } from "@/components/wishlist/WishlistButton";
import busterPunchPlaceholder from "@/assets/buster-punch-placeholder.png";
import { getPrimaryImageIndexOverride } from "@/lib/productImageOverrides";
import { getLocalizedTitle, type Language } from "@/lib/productTranslations";
import { buildProductPath } from "@/lib/productUrl";
import { CopyButton } from "@/components/ui/copy-button";

// Optimized image URL with Shopify CDN sizing
const getOptimizedImageUrl = (url: string, width: number = 400): string => {
  if (!url) return url;
  // Shopify CDN image optimization - request specific size
  if (url.includes('cdn.shopify.com')) {
    // Remove any existing size suffix and add new one
    const cleanUrl = url.replace(/_\d+x\d*\./, '.');
    return cleanUrl.replace(/\.([^.]+)$/, `_${width}x.$1`);
  }
  return url;
};

interface ProductCardProps {
  product: ShopifyProduct;
  index?: number;
  initialFinish?: string;
  searchedSku?: string;
}

export const ProductCard = ({ product, index = 0, initialFinish, searchedSku }: ProductCardProps) => {
  const { getLocalizedPath, language } = useLanguage();
  const { node } = product;

  // Lazy-loaded full variant/image data for products with 10+ finishes
  const [fullData, setFullData] = useState<{ images: typeof node.images; variants: typeof node.variants } | null>(null);
  const fullDataFetchedRef = useRef(false);

  // Use full data if loaded, otherwise use the initial (partial) data
  const activeVariants = fullData?.variants || node.variants;
  const activeImages = fullData?.images || node.images;
  
  const finishOption = node.options.find(opt => opt.name.toLowerCase() === 'finish');
  const finishValues = finishOption?.values || [];
  
  // Find matching finish from initialFinish prop, or use first available
  const getMatchingFinish = (targetFinish: string | undefined) => {
    if (targetFinish && finishValues.length > 0) {
      const upperTarget = targetFinish.toUpperCase();
      const targetIsDual = upperTarget.includes('/');
      
      const match = finishValues.find(f => {
        const upperF = f.toUpperCase();
        const productIsDual = upperF.includes('/');
        
        // Exact match
        if (upperF === upperTarget) return true;
        
        // Normalize for comparison
        const normalizedF = upperF.replace(/\s*\/\s*/g, '/');
        const normalizedTarget = upperTarget.replace(/\s*\/\s*/g, '/');
        if (normalizedF === normalizedTarget) return true;
        
        // If target is single and product has dual - match if it contains the target
        if (!targetIsDual && productIsDual) {
          const parts = upperF.split('/').map(p => p.trim());
          return parts.some(p => p === upperTarget);
        }
        
        return false;
      });
      if (match) return match;
    }
    return finishValues[0] || '';
  };
  
  // If a searched SKU is provided, find the matching variant's finish
  const skuMatchedFinish = useMemo(() => {
    if (!searchedSku) return undefined;
    const skuLower = searchedSku.toLowerCase();
    for (const edge of activeVariants.edges) {
      if (edge.node.sku?.toLowerCase() === skuLower) {
        const finishOpt = edge.node.selectedOptions?.find(o => o.name.toLowerCase() === 'finish');
        return finishOpt?.value;
      }
    }
    return undefined;
  }, [searchedSku, activeVariants.edges]);

  const [selectedFinish, setSelectedFinish] = useState<string>(getMatchingFinish(skuMatchedFinish || initialFinish));
  
  // Update selected finish when initialFinish prop changes
  useEffect(() => {
    if (initialFinish) {
      const match = getMatchingFinish(initialFinish);
      if (match && match !== selectedFinish) {
        setSelectedFinish(match);
      }
    }
  }, [initialFinish]);
  
  // Touch swipe handling for mobile
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null || finishValues.length <= 1) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchEndX - touchStartX.current;
    const diffY = touchEndY - touchStartY.current;
    
    // Only trigger if horizontal swipe is more significant than vertical
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30) {
      e.preventDefault();
      const currentIndex = finishValues.indexOf(selectedFinish);
      
      if (diffX < 0) {
        // Swipe left - next finish
        const nextIndex = (currentIndex + 1) % finishValues.length;
        setSelectedFinish(finishValues[nextIndex]);
      } else {
        // Swipe right - previous finish
        const prevIndex = currentIndex <= 0 ? finishValues.length - 1 : currentIndex - 1;
        setSelectedFinish(finishValues[prevIndex]);
      }
    }
    
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const selectedVariant = useMemo(() => {
    if (!selectedFinish) return activeVariants.edges[0]?.node;
    
    const variant = activeVariants.edges.find(v => 
      v.node.selectedOptions.some(
        opt => opt.name.toLowerCase() === 'finish' && opt.value === selectedFinish
      )
    );
    return variant?.node || activeVariants.edges[0]?.node;
  }, [selectedFinish, activeVariants.edges]);

  // Lazy-load full variant data if finish filter is active but variant not found in initial batch
  useEffect(() => {
    if (!initialFinish || fullDataFetchedRef.current || fullData) return;
    // Check if the selected finish variant was actually found
    const hasMatchingVariant = activeVariants.edges.some(v =>
      v.node.selectedOptions.some(
        opt => opt.name.toLowerCase() === 'finish' && opt.value === selectedFinish
      )
    );
    // Also check if we have enough images to match (if only 5 loaded and finish not found in images)
    const needsMoreData = !hasMatchingVariant || (finishValues.length > 10 && activeImages.edges.length <= 5);
    if (!needsMoreData) return;

    fullDataFetchedRef.current = true;
    fetchSingleProductVariants(node.handle).then(data => {
      if (data) setFullData(data);
    });
  }, [initialFinish, selectedFinish, node.handle, fullData, activeVariants.edges, activeImages.edges.length, finishValues.length]);

  const variantImage = useMemo(() => {
    // Explicit per-product override (e.g. first image is a table)
    const overrideIndex = getPrimaryImageIndexOverride(node.handle);
    if (overrideIndex !== undefined && activeImages.edges[overrideIndex]?.node) {
      return activeImages.edges[overrideIndex].node;
    }

    // Helper to check if image is technical/table drawing
    const looksLikeTechnical = (text: string) => /technical|drawing|diagram|specification|wymiar|rys|tabela|table|schemat|schema|pasuje\s+do/i.test(text);

    const isTechnicalImage = (img: { altText?: string | null; url: string }) => {
      const alt = img.altText || '';
      const url = img.url;
      return looksLikeTechnical(alt) || looksLikeTechnical(url);
    };

    const normalizeText = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const textIncludesAllTokens = (text: string, tokens: string[]) => tokens.every(token => text.includes(token));

    // Filter out technical images first
    const nonTechnicalImages = activeImages.edges.filter(img => !isTechnicalImage(img.node));
    const imagesToSearch = nonTechnicalImages.length > 0 ? nonTechnicalImages : activeImages.edges;

    if (!selectedFinish) return imagesToSearch[0]?.node || activeImages.edges[0]?.node;

    const finishTokens = normalizeText(selectedFinish).split(' ').filter(Boolean);
    const skuTokens = normalizeText(selectedVariant?.sku || '').split(' ').filter(Boolean);

    // First, try exact SKU match from the product gallery.
    if (skuTokens.length > 0) {
      const skuPackshotMatch = imagesToSearch.find(img => {
        const alt = img.node.altText || '';
        const url = img.node.url;
        const text = normalizeText(`${alt} ${url}`);
        const isPackshot1 = /packshot[-\s]?1/i.test(alt) || /packshot[-\s]?1/i.test(url);
        return isPackshot1 && textIncludesAllTokens(text, skuTokens);
      });

      if (skuPackshotMatch) return skuPackshotMatch.node;

      const skuMatch = imagesToSearch.find(img => {
        const alt = img.node.altText || '';
        const url = img.node.url;
        const text = normalizeText(`${alt} ${url}`);
        return textIncludesAllTokens(text, skuTokens);
      });

      if (skuMatch) return skuMatch.node;
    }

    const finishPackshotMatch = imagesToSearch.find(img => {
      const alt = img.node.altText || '';
      const url = img.node.url;
      const text = normalizeText(`${alt} ${url}`);
      const isPackshot1 = /packshot[-\s]?1/i.test(alt) || /packshot[-\s]?1/i.test(url);
      return isPackshot1 && textIncludesAllTokens(text, finishTokens);
    });

    if (finishPackshotMatch) return finishPackshotMatch.node;

    const finishMatch = imagesToSearch.find(img => {
      const alt = img.node.altText || '';
      const url = img.node.url;
      const text = normalizeText(`${alt} ${url}`);
      return textIncludesAllTokens(text, finishTokens);
    });

    if (finishMatch) return finishMatch.node;

    // Final fallback: if the selected variant has its own image from Shopify, use it.
    if (selectedVariant?.image?.url) {
      return selectedVariant.image;
    }

    return imagesToSearch[0]?.node || activeImages.edges[0]?.node;
  }, [selectedFinish, selectedVariant, node.handle, activeImages.edges]);

  const price = node.priceRange.minVariantPrice;
  const hasActiveSalePrice = Boolean(
    selectedVariant?.compareAtPrice &&
    parseFloat(selectedVariant.compareAtPrice.amount) > parseFloat(selectedVariant.price.amount)
  );

  const handleFinishClick = (e: React.MouseEvent, finish: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFinish(finish);
  };

  // Check if product is UK version (but not for Accessories/Jewellery/Kitchen/Hardware which don't need regional distinction)
  const isAccessory = node.tags?.includes('Accessories') || node.productType === 'Jewellery';
  const isKitchen = node.tags?.some(tag => tag.toLowerCase().startsWith('kitchen'));
  const isHardware = node.tags?.includes('Hardware');
  const isUKProduct = !isAccessory && !isKitchen && !isHardware && (node.tags?.includes('UK') || node.title.includes('(UK)') || /\bUK\b/.test(node.title)) && !/\bEURO\b/i.test(node.title);
  
  // Check if product is from Deante - they have white background images
  const isDeante = node.vendor?.toLowerCase() === 'deante';
  
  // Check if product is from Buster + Punch (dark styled images)
  const isBusterPunch = node.vendor?.toUpperCase().includes('BUSTER') && node.vendor?.toUpperCase().includes('PUNCH');
  
  // Products that need white background for images (all except Buster + Punch which has dark-styled images)
  const needsWhiteBackground = !isBusterPunch;
  
  // Get localized title first, then clean for accessories/hardware
  const localizedTitle = getLocalizedTitle(node, language as Language);
  const displayTitle = (isAccessory || isKitchen || isHardware) 
    ? localizedTitle.replace(/\s*\(UK\)\s*$/i, '').trim() 
    : localizedTitle;

  // Build product URL with SKU for direct variant linking.
  // Includes category/subcategory slugs in the path for SEO.
  const productUrl = getLocalizedPath(
    buildProductPath(node, { sku: selectedVariant?.sku || null })
  );

  return (
    <Link
      to={productUrl}
      className="group block animate-slide-up h-full"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <article className="relative bg-charcoal-light rounded-2xl overflow-hidden border border-cream/10 hover:border-gold/30 transition-all duration-500 hover:shadow-elevated h-full flex flex-col">
        {/* Badges container */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
          {/* Sale Badge with countdown - combined in one element */}
          {node.tags?.some(tag => tag.toLowerCase() === 'sale') && (
            <div className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-md shadow-lg flex items-center gap-1.5">
              <span>SALE</span>
            </div>
          )}
          {/* UK Badge */}
          {isUKProduct && (
            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-md shadow-lg">
              UK
            </span>
          )}
        </div>

        {/* Wishlist + copy link buttons */}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
          <CopyButton
            value={`${typeof window !== 'undefined' ? window.location.origin : 'https://haus.lv'}${productUrl}`}
            label="Copy link"
            successMessage="Link copied"
            className="bg-charcoal/70 backdrop-blur-sm text-cream/70 hover:text-gold p-2 rounded-full"
            iconClassName="h-3.5 w-3.5"
          />
          <WishlistButton
            product={product}
            variantId={selectedVariant?.id || node.variants.edges[0]?.node.id}
            variantTitle={selectedVariant?.title || node.variants.edges[0]?.node.title || 'Default Title'}
            price={{
              amount: selectedVariant?.price.amount || price.amount,
              currencyCode: selectedVariant?.price.currencyCode || price.currencyCode,
            }}
            compareAtPrice={selectedVariant?.compareAtPrice || null}
            selectedOptions={selectedVariant?.selectedOptions || []}
            sku={selectedVariant?.sku}
            variantImage={variantImage ? { url: variantImage.url, altText: variantImage.altText } : undefined}
            className="bg-charcoal/50 hover:bg-charcoal/80 backdrop-blur-sm"
          />
        </div>
        
        {/* Image - swipeable on mobile, white bg for products with white-background images */}
        <div 
          className={`relative aspect-square overflow-hidden flex-shrink-0 ${needsWhiteBackground ? 'bg-white' : 'bg-charcoal'}`}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {variantImage ? (
            <img
              src={getOptimizedImageUrl(variantImage.url, 400)}
              alt={variantImage.altText || node.title}
              width={400}
              height={400}
              loading="lazy"
              decoding="async"
              fetchPriority={index !== undefined && index < 8 ? "high" : "auto"}
              className={`w-full h-full object-contain transition-transform duration-700 group-hover:scale-105 ${isDeante ? 'p-3' : ''}`}
            />
          ) : node.vendor?.toUpperCase().includes('BUSTER') && node.vendor?.toUpperCase().includes('PUNCH') ? (
            <img
              src={busterPunchPlaceholder}
              alt={node.title}
              width={400}
              height={400}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-cream/5">
              <span className="text-cream/30 text-sm font-body">No image</span>
            </div>
          )}
          {/* Swipe indicator dots for mobile */}
          {finishValues.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 md:hidden">
              {finishValues.map((finish, i) => (
                <span
                  key={finish}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    selectedFinish === finish ? 'bg-white w-3' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content - fixed height sections */}
        <div className="p-4 flex flex-col flex-1">
          {/* Vendor/Brand - fixed height */}
          <div className="h-5 mb-1">
            {node.vendor && (
              <p className="text-xs font-medium text-gold uppercase tracking-wider truncate">
                {node.vendor}
              </p>
            )}
          </div>
          
          {/* Finish swatches - moved above title */}
          <div className="h-7 mb-2">
            {finishValues.length > 1 && (
              <div className="flex items-center gap-1.5">
                {finishValues.slice(0, 4).map((finish) => {
                  const isSelected = selectedFinish === finish;
                   const isDual = isDualFinish(finish);
                   const size = 24;
                  
                  // Check if any part of the finish is dark
                  const darkFinishes = ['BLACK', 'BURNT STEEL', 'GUN METAL', 'SMOKED BRONZE', 'NERO'];
                  const isDark = darkFinishes.some(df => finish.toUpperCase().includes(df));
                  const outerGlow = isDark ? ', 0 0 0 1px rgba(255,255,255,0.15)' : '';
                  
                  // For Deante products, use texture images
                  const deanteImage = isDeante ? getDeanteFinishImage(finish) : null;
                  
                  if (deanteImage) {
                    return (
                      <button
                        key={finish}
                        onClick={(e) => handleFinishClick(e, finish)}
                        className={`flex-shrink-0 rounded-full transition-all duration-200 overflow-hidden ${!isSelected ? 'hover:scale-110' : ''}`}
                        style={{
                          width: size,
                          height: size,
                          boxShadow: isSelected
                            ? `0 0 0 2px rgba(212,168,75,0.9), 0 4px 8px rgba(0,0,0,0.4)${outerGlow}`
                            : `0 2px 4px rgba(0,0,0,0.25)${outerGlow}`,
                          border: isSelected ? '2px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.1)'
                        }}
                        title={finish}
                        aria-label={`Select ${finish} finish`}
                      >
                        <img
                          src={deanteImage}
                          alt={finish}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  }
                  
                  if (isDual) {
                    const parts = finish.split('/').map(p => p.trim());
                    const style1 = getFinishStyle(parts[0] || '');
                    const style2 = getFinishStyle(parts[1] || parts[0] || '');
                    
                    return (
                      <button
                        key={finish}
                        onClick={(e) => handleFinishClick(e, finish)}
                        className={`flex-shrink-0 rounded-full transition-all duration-200 overflow-hidden ${!isSelected ? 'hover:scale-110' : ''}`}
                        style={{
                          width: size,
                          height: size,
                          position: 'relative',
                          boxShadow: isSelected
                            ? `0 0 0 2px rgba(212,168,75,0.9), 0 4px 8px rgba(0,0,0,0.4)${outerGlow}`
                            : `0 2px 4px rgba(0,0,0,0.25)${outerGlow}`
                        }}
                        title={finish}
                        aria-label={`Select ${finish} finish`}
                      >
                        {/* Left half */}
                        <span
                          className="absolute top-0 left-0 h-full"
                          style={{
                            width: '50%',
                            background: style1.background,
                            boxShadow: style1.boxShadow,
                          }}
                        />
                        {/* Right half */}
                        <span
                          className="absolute top-0 right-0 h-full"
                          style={{
                            width: '50%',
                            background: style2.background,
                            boxShadow: style2.boxShadow,
                          }}
                        />
                      </button>
                    );
                  }
                  
                  const finishStyle = getFinishStyle(finish);
                  return (
                    <button
                      key={finish}
                      onClick={(e) => handleFinishClick(e, finish)}
                      className={`flex-shrink-0 rounded-full transition-all duration-200 ${!isSelected ? 'hover:scale-110' : ''}`}
                      style={{ 
                        width: size,
                        height: size,
                        background: finishStyle.background,
                        boxShadow: isSelected
                          ? `0 0 0 2px rgba(212,168,75,0.9), ${finishStyle.boxShadow}, 0 4px 8px rgba(0,0,0,0.4)${outerGlow}`
                          : `${finishStyle.boxShadow}, 0 2px 4px rgba(0,0,0,0.25)${outerGlow}`
                      }}
                      title={finish}
                      aria-label={`Select ${finish} finish`}
                    />
                  );
                })}
                {finishValues.length > 4 && (
                  <span className="flex-shrink-0 text-xs text-cream/40 ml-0.5">
                    +{finishValues.length - 4}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Title - flexible height */}
          <h3 className="font-display text-sm font-semibold text-cream mb-2 group-hover:text-gold transition-colors duration-300 leading-tight min-h-[2.5rem]">
            {displayTitle}
          </h3>
          


          {/* Price and SKU - pushed to bottom */}
          <div className="mt-auto">
            {/* Sale price logic: show original crossed out + sale price */}
            {hasActiveSalePrice ? (
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                <p className="font-display text-xs sm:text-sm text-cream/50 line-through">
                  {formatPrice(selectedVariant.compareAtPrice.amount, selectedVariant.compareAtPrice.currencyCode)}
                </p>
                <p className="font-display font-bold text-base sm:text-lg text-cream">
                  {formatPrice(selectedVariant.price.amount, selectedVariant.price.currencyCode)}
                </p>
              </div>
            ) : (
              <p className="font-display font-bold text-lg text-cream">
                {formatPrice(
                  selectedVariant?.price.amount || price.amount,
                  selectedVariant?.price.currencyCode || price.currencyCode
                )}
              </p>
            )}
            
            <div className="h-5 mt-1 flex items-center gap-1">
              {selectedVariant?.sku && (
                <>
                  <p className="text-xs text-cream/50 truncate">
                    SKU: {selectedVariant.sku}
                  </p>
                  <CopyButton
                    value={selectedVariant.sku}
                    label="Copy SKU"
                    successMessage={`SKU ${selectedVariant.sku}`}
                    className="text-cream/50 hover:text-gold shrink-0"
                    iconClassName="h-3 w-3"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
};
